import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { List } from '@refinedev/antd'
import { Table, Space, Avatar, Tag, Button, Typography, Card, Row, Col, Statistic, Alert, Badge, Tooltip, Tabs } from 'antd'
import {
  PhoneOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  UserOutlined,
  MailOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  VideoCameraOutlined,
  MedicineBoxOutlined,
} from '@ant-design/icons'

const { Text, Paragraph } = Typography

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3520/api'

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('admin_access_token')
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  }
}

interface PendingConsultation {
  id: string
  workflowInstanceId: string
  workflowType: string | null
  isStandalone: boolean
  patient: {
    id: string
    email: string
    firstName: string | null
    lastName: string | null
    phone: string | null
    image: string | null
  }
  requestedAt: string
  dueAt: string | null
  isOverdue: boolean
  hoursWaiting: number
  // Request data from patient
  serviceType?: string
  reason?: string
  preferredTimes?: string[]
  contactPreference?: 'PHONE' | 'EMAIL'
}

interface ScheduledConsultation {
  id: string
  patient: {
    id: string
    name: string
    email: string
    phone: string | null
    image: string | null
  }
  doctor: {
    id: string
    name: string
    image: string | null
  }
  scheduledAt: string
  duration: number
  status: string
  statusLabel: string
  roomUrl: string | null
  type: string
  typeLabel: string
}

export function ConsultationsListPage() {
  const [activeTab, setActiveTab] = useState('pending')

  // Pending state
  const [pendingData, setPendingData] = useState<PendingConsultation[]>([])
  const [pendingTotal, setPendingTotal] = useState(0)
  const [pendingLoading, setPendingLoading] = useState(true)
  const [pendingPage, setPendingPage] = useState(1)

  // Scheduled state
  const [scheduledData, setScheduledData] = useState<ScheduledConsultation[]>([])
  const [scheduledTotal, setScheduledTotal] = useState(0)
  const [scheduledLoading, setScheduledLoading] = useState(true)
  const [scheduledPage, setScheduledPage] = useState(1)

  const pageSize = 10

  const fetchPending = async (page: number) => {
    setPendingLoading(true)
    try {
      const res = await fetch(`${API_URL}/admin/consultations/pending?page=${page}&pageSize=${pageSize}`, {
        headers: getAuthHeaders(),
      })
      if (!res.ok) throw new Error('Failed to fetch')
      const json = await res.json()
      setPendingData(json.data)
      setPendingTotal(json.total)
    } catch (error) {
      console.error('Error fetching pending consultations:', error)
    } finally {
      setPendingLoading(false)
    }
  }

  const fetchScheduled = async (page: number) => {
    setScheduledLoading(true)
    try {
      const res = await fetch(`${API_URL}/admin/consultations/scheduled?page=${page}&pageSize=${pageSize}`, {
        headers: getAuthHeaders(),
      })
      if (!res.ok) throw new Error('Failed to fetch')
      const json = await res.json()
      setScheduledData(json.data)
      setScheduledTotal(json.total)
    } catch (error) {
      console.error('Error fetching scheduled consultations:', error)
    } finally {
      setScheduledLoading(false)
    }
  }

  useEffect(() => {
    fetchPending(pendingPage)
    fetchScheduled(scheduledPage)
  }, [])

  useEffect(() => {
    fetchPending(pendingPage)
  }, [pendingPage])

  useEffect(() => {
    fetchScheduled(scheduledPage)
  }, [scheduledPage])

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleString('sr-Latn-RS', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getWaitingStatus = (hours: number, isOverdue: boolean) => {
    if (isOverdue) {
      return { color: 'error' as const, text: `${hours}h - Kasni!`, icon: <ExclamationCircleOutlined /> }
    }
    if (hours >= 24) {
      return { color: 'warning' as const, text: `${hours}h`, icon: <ClockCircleOutlined /> }
    }
    return { color: 'default' as const, text: `${hours}h`, icon: <ClockCircleOutlined /> }
  }

  const overdueCount = pendingData.filter(c => c.isOverdue).length
  const urgentCount = pendingData.filter(c => c.hoursWaiting >= 24 && !c.isOverdue).length

  const statusColors: Record<string, string> = {
    SCHEDULED: 'blue',
    CONFIRMED: 'cyan',
    IN_PROGRESS: 'orange',
    COMPLETED: 'green',
    CANCELLED: 'default',
  }

  const pendingContent = (
    <>
      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Ukupno ceka"
              value={pendingTotal}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#1677ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Hitno (>24h)"
              value={urgentCount}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Kasni"
              value={overdueCount}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Instructions */}
      <Alert
        message="Kako zakazati konsultaciju"
        description={
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            <li>Pozovite pacijenta telefonom i dogovorite termin</li>
            <li>Kliknite na dugme &quot;Zakazi&quot; u tabeli</li>
            <li>Izaberite doktora i termin, zatim potvrdite</li>
          </ol>
        }
        type="info"
        showIcon
        icon={<PhoneOutlined />}
        style={{ marginBottom: 16 }}
      />

      {/* Table */}
      <Table
        dataSource={pendingData}
        loading={pendingLoading}
        rowKey="id"
        pagination={{
          current: pendingPage,
          pageSize,
          total: pendingTotal,
          onChange: setPendingPage,
          showSizeChanger: false,
          showTotal: (total, range) => `${range[0]}-${range[1]} od ${total} zahteva`,
        }}
        locale={{
          emptyText: (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
              <Paragraph>Nema zahteva za konsultacije koji cekaju</Paragraph>
            </div>
          ),
        }}
      >
        <Table.Column
          dataIndex={['patient', 'firstName']}
          title="Pacijent"
          render={(_, record: PendingConsultation) => (
            <Space>
              <Avatar
                src={record.patient.image}
                icon={!record.patient.image && <UserOutlined />}
                style={{ backgroundColor: '#1677ff' }}
              />
              <div>
                <Link to={`/consultations/${record.id}`}>
                  <Text strong style={{ color: '#1677ff', cursor: 'pointer' }}>
                    {record.patient.firstName || record.patient.lastName
                      ? `${record.patient.firstName || ''} ${record.patient.lastName || ''}`.trim()
                      : record.patient.email}
                  </Text>
                </Link>
              </div>
            </Space>
          )}
        />
        <Table.Column
          dataIndex={['patient', 'email']}
          title="Kontakt"
          render={(email: string, record: PendingConsultation) => (
            <Space direction="vertical" size={0}>
              <Space size={4}>
                <MailOutlined style={{ color: '#8c8c8c' }} />
                <Text>{email}</Text>
              </Space>
              {record.patient.phone && (
                <Space size={4}>
                  <PhoneOutlined style={{ color: '#8c8c8c' }} />
                  <a href={`tel:${record.patient.phone}`}>{record.patient.phone}</a>
                </Space>
              )}
            </Space>
          )}
        />
        <Table.Column
          dataIndex="serviceType"
          title="Usluga"
          render={(serviceType: string | undefined, record: PendingConsultation) => {
            const serviceLabels: Record<string, string> = {
              trt: 'TRT',
              general: 'Opšte',
              dermatology: 'Dermatologija',
              menopause: 'Menopauza',
            }
            const contactIcon = record.contactPreference === 'EMAIL' ? <MailOutlined /> : <PhoneOutlined />
            return (
              <Space direction="vertical" size={0}>
                <Tag color={serviceType === 'trt' ? 'green' : 'blue'}>
                  {serviceLabels[serviceType || ''] || serviceType || 'TRT'}
                </Tag>
                <Tooltip title={record.contactPreference === 'EMAIL' ? 'Preferira email' : 'Preferira telefon'}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {contactIcon} {record.contactPreference === 'EMAIL' ? 'Email' : 'Telefon'}
                  </Text>
                </Tooltip>
              </Space>
            )
          }}
        />
        <Table.Column
          dataIndex="requestedAt"
          title="Zatrazeno"
          render={(date: string) => formatDateTime(date)}
        />
        <Table.Column
          dataIndex="hoursWaiting"
          title="Ceka"
          render={(hours: number, record: PendingConsultation) => {
            const status = getWaitingStatus(hours, record.isOverdue)
            return (
              <Tooltip title={record.isOverdue ? 'Prekoracen SLA od 48h' : undefined}>
                <Tag color={status.color} icon={status.icon}>
                  {status.text}
                </Tag>
              </Tooltip>
            )
          }}
        />
        <Table.Column
          key="actions"
          title="Akcije"
          render={(_, record: PendingConsultation) => (
            <Space>
              {record.patient.phone && (
                <Tooltip title="Pozovi">
                  <Button
                    type="text"
                    icon={<PhoneOutlined />}
                    href={`tel:${record.patient.phone}`}
                  />
                </Tooltip>
              )}
              <Link to={`/consultations/${record.id}`}>
                <Button type="primary" size="small">
                  Zakazi
                </Button>
              </Link>
            </Space>
          )}
        />
      </Table>
    </>
  )

  const scheduledContent = (
    <Table
      dataSource={scheduledData}
      loading={scheduledLoading}
      rowKey="id"
      pagination={{
        current: scheduledPage,
        pageSize,
        total: scheduledTotal,
        onChange: setScheduledPage,
        showSizeChanger: false,
        showTotal: (total, range) => `${range[0]}-${range[1]} od ${total} zakazanih`,
      }}
      locale={{
        emptyText: (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <CalendarOutlined style={{ fontSize: 48, color: '#8c8c8c', marginBottom: 16 }} />
            <Paragraph>Nema zakazanih konsultacija</Paragraph>
          </div>
        ),
      }}
    >
      <Table.Column
        title="Pacijent"
        render={(_, record: ScheduledConsultation) => (
          <Space>
            <Avatar
              src={record.patient.image}
              icon={!record.patient.image && <UserOutlined />}
              style={{ backgroundColor: '#1677ff' }}
            />
            <div>
              <Link to={`/patients/${record.patient.id}`}>
                <Text strong style={{ color: '#1677ff' }}>
                  {record.patient.name}
                </Text>
              </Link>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>{record.patient.email}</Text>
            </div>
          </Space>
        )}
      />
      <Table.Column
        title="Lekar"
        render={(_, record: ScheduledConsultation) => (
          <Space>
            <Avatar
              src={record.doctor.image}
              icon={!record.doctor.image && <MedicineBoxOutlined />}
              style={{ backgroundColor: '#52c41a' }}
            />
            <Text>{record.doctor.name}</Text>
          </Space>
        )}
      />
      <Table.Column
        dataIndex="scheduledAt"
        title="Termin"
        render={(date: string) => (
          <Space>
            <CalendarOutlined />
            {formatDateTime(date)}
          </Space>
        )}
      />
      <Table.Column
        dataIndex="duration"
        title="Trajanje"
        render={(duration: number) => `${duration} min`}
      />
      <Table.Column
        dataIndex="status"
        title="Status"
        render={(status: string, record: ScheduledConsultation) => (
          <Tag color={statusColors[status] || 'default'}>
            {record.statusLabel}
          </Tag>
        )}
      />
      <Table.Column
        key="actions"
        title="Akcije"
        render={(_, record: ScheduledConsultation) => {
          // Button routes through the in-app call page so the server can mint
          // a scoped meeting token + JIT-create the Daily room — never link
          // directly to record.roomUrl (it can be null/stale). The button is
          // live only inside the join window: from 15 min before scheduledAt
          // until 15 min after the scheduled end (matches server-side
          // AppointmentService.canJoin).
          const scheduledAt = new Date(record.scheduledAt).getTime()
          const joinStart = scheduledAt - 15 * 60 * 1000
          const joinEnd = scheduledAt + (record.duration + 15) * 60 * 1000
          const now = Date.now()
          const isLive = now >= joinStart && now <= joinEnd
          const isPast = now > joinEnd
          return (
            <Space>
              <Link to={`/patients/${record.patient.id}`}>
                <Button size="small">Detalji</Button>
              </Link>
              <Tooltip title={isLive ? 'Video poziv' : isPast ? 'Prošlo' : 'Još nije vreme'}>
                <Link to={`/my-consultations/${record.id}/call`}>
                  <Button
                    type="primary"
                    size="small"
                    icon={<VideoCameraOutlined />}
                    disabled={!isLive}
                  />
                </Link>
              </Tooltip>
            </Space>
          )
        }}
      />
    </Table>
  )

  return (
    <List
      title="Konsultacije"
      headerButtons={
        <Space>
          <Badge count={pendingTotal} showZero color="#faad14">
            <Tag>Ceka</Tag>
          </Badge>
          <Badge count={scheduledTotal} showZero color="#1677ff">
            <Tag>Zakazano</Tag>
          </Badge>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              fetchPending(pendingPage)
              fetchScheduled(scheduledPage)
            }}
          >
            Osvezi
          </Button>
        </Space>
      }
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'pending',
            label: (
              <Space>
                <ClockCircleOutlined />
                Cekaju ({pendingTotal})
              </Space>
            ),
            children: pendingContent,
          },
          {
            key: 'scheduled',
            label: (
              <Space>
                <CalendarOutlined />
                Zakazane ({scheduledTotal})
              </Space>
            ),
            children: scheduledContent,
          },
        ]}
      />
    </List>
  )
}

export default ConsultationsListPage

export { default as ConsultationShowPage } from './show'
